//#region Util Imports

import * as _ from 'underscore'

//#endregion

import { Dictionary, KeyType, DataProvider, LogHelper } from "../../helpers"

import { DataProviderRequestCacheStrategy } from "../data-provider.interfaces"

export type IndexedParamCacheStrategyOptions<TParams> = {
    getIndexedParams: (sourceParams: TParams) => number[]
    setIndexedParams: (sourceParams: TParams, indexedParams: number[]) => void
    ignoredIndexes?: number[]
}

export class IndexedParamCacheStrategy<TParams> implements DataProviderRequestCacheStrategy<TParams> {

    //#region Consts

    private get LOG_PREFIX() { return '[SIMAX.DataProvider.CacheStrategy.IndexedParam]' }

    //#endregion

    //#region Members

    private static requestCache: Dictionary<Dictionary<JQueryPromise<any>>> = null

    private requestMethod: string
    private resultField: string
    private dataProvider: DataProvider
    private requestParams: TParams

    //#endregion

    //#region Ctor

    constructor(private options: IndexedParamCacheStrategyOptions<TParams>) {
        if (IndexedParamCacheStrategy.requestCache == null) [
            IndexedParamCacheStrategy.requestCache = new Dictionary<Dictionary<JQueryPromise<any>>>(null, KeyType.Number)
        ]
    }

    //#endregion

    public request(dataProvider: DataProvider, requestMethod: string, requestParams: TParams, resultField: string): JQueryPromise<any> {
        this.dataProvider = dataProvider
        this.requestMethod = requestMethod
        this.requestParams = requestParams
        this.resultField = resultField

        const paramsToCache: number[] = this.getCachedParams(this.requestParams)

        if (paramsToCache != null) {
            const nonCachedParams: number[] = this.filterNonCachedParams(paramsToCache)

            return this
                .requestAndCachePromise(nonCachedParams)
                .then(() => {
                    return this
                        .getParamsFromCache(paramsToCache)
                        .fail((err: any) => {
                            LogHelper.warn(this.LOG_PREFIX, `Unable to load params for method: ${this.requestMethod}.`, err)

                            return $.Deferred().reject(err).promise()
                        })
                })
        }

        return this.sendRequest(this.requestParams)
    }

    private requestAndCachePromise(params: number[]): JQueryPromise<any> {
        const deferred = $.Deferred()

        if (_.size(params) > 0) {
            this.requestAndCache(params)
                .always(() => {
                    deferred.resolve()
                })
        }
        else {
            deferred.resolve()
        }

        return deferred.promise()
    }

    private requestAndCache(params: number[]): JQueryPromise<any> {
        const requestParams: TParams = this.setCachedParams(this.requestParams, params)
        const resultPromise: JQueryPromise<any> = this.sendRequest(requestParams)

        _.forEach(params, (param: number, paramIndex: number) => {
            const cachePromise = resultPromise.then((loadedData: any[]) => {
                return loadedData[paramIndex]
            })

            this.addPromiseToCache(param, cachePromise)
        })

        return resultPromise
    }

    private sendRequest(params: TParams): JQueryPromise<any> {
        return this.dataProvider.sendRequestWithSession(this.requestMethod, params, this.resultField)
    }

    //#region Cache

    private addPromiseToCache(param: number, cachePromise: JQueryPromise<any>): void {
        return this.getCache().addOrUpdate(param, cachePromise)
    }

    private filterNonCachedParams(params: number[]): number[] {
        const cacheKeys: number[] = this.getCacheKeys()

        return _.filter(params, (param: number) => {
            return !_.contains(cacheKeys, param) || (_.indexOf(this.options.ignoredIndexes, param) > -1)
        })
    }

    private getCacheKeys(): number[] {
        return this.getCache().keys() as number[]
    }

    private getPromiseFromCache(param: number): JQueryPromise<any> {
        return this.getCache().tryGetValue(param)
    }

    private getParamsFromCache(params: number[]): JQueryPromise<any> {
        const cachedPromises = _.map(params, (param: number) => {
            return this.getPromiseFromCache(param)
        })

        return $.when(...cachedPromises).then((...r) => r)
    }

    private getCache(): Dictionary<JQueryPromise<any>> {
        this.ensureCacheDictionaryExists()

        return IndexedParamCacheStrategy.requestCache.tryGetValue(this.requestMethod)
    }

    private ensureCacheDictionaryExists(): void {
        if (!IndexedParamCacheStrategy.requestCache.containsKey(this.requestMethod)) {
            IndexedParamCacheStrategy.requestCache.add(this.requestMethod, new Dictionary<JQueryPromise<any>>(null, KeyType.Number))
        }
    }

    //#endregion

    //#region Options

    private getCachedParams(originalParams: TParams): number[] {
        return this.options.getIndexedParams(originalParams)
    }

    private setCachedParams(originalParams: any, cachedParams: number[]): TParams {
        this.options.setIndexedParams(originalParams, cachedParams)

        return originalParams
    }

    //#endregion
}